/**
 * Live feature meters — the instrument Phases 1 and 4 are accepted on.
 *
 * Both phases state their criteria as numbers with thresholds ("aperture under
 * 0.1 at rest, over 0.7 on an open ah"; "a whistle drives flatness below 0.15"),
 * so the meters draw those thresholds as marks on the bar. A meter you have to
 * squint at and estimate against is a meter that will agree with whatever you
 * already believe.
 *
 * The audio meters additionally hold their extremes. A plosive burst is ten to
 * twenty milliseconds long and the display refreshes every sixteen: a bar that
 * only shows the current value cannot show you a transient at all, and you would
 * be left judging a clap by whether the bar seemed to twitch. Peak-hold is not a
 * convenience here, it is the only way the acceptance test is readable.
 */

const fixed = (dp) => (v) => v.toFixed(dp);
const hz = (v) => `${Math.round(v)} Hz`;
const dB = (v) => `${v.toFixed(1)} dB`;

/** Phase 1: mouth state, all in 0..1. */
export const MOUTH_METERS = [
  {
    key: 'aperture',
    label: 'aperture',
    hint: 'jaw + lip gap',
    marks: [
      { at: 0.1, label: 'rest' },
      { at: 0.7, label: 'ah' },
    ],
  },
  { key: 'width', label: 'width', hint: 'oo … eee' },
  { key: 'lipJitter', label: 'lipJitter', hint: 'trill place cue' },
  { key: 'jawOpen', label: 'jawOpen', hint: 'raw' },
  { key: 'close', label: 'close', hint: 'raw · plosive gate' },
  { key: 'pucker', label: 'pucker', hint: 'raw' },
  { key: 'funnel', label: 'funnel', hint: 'raw' },
  { key: 'smile', label: 'smile', hint: 'raw' },
  { key: 'stretch', label: 'stretch', hint: 'raw' },
  { key: 'press', label: 'press', hint: 'raw' },
  { key: 'lowerDown', label: 'lowerDown', hint: 'raw' },
];

/**
 * Phase 4: audio features, on the scales they actually live on.
 *
 * `hold` names the extreme worth keeping. Flatness holds its minimum because
 * the whistle criterion is a floor; everything else holds its maximum.
 */
export const AUDIO_METERS = [
  { key: 'db', label: 'level', hint: 'rms', min: -80, max: 0, format: dB, hold: 'max' },
  {
    key: 'attack',
    label: 'attack',
    hint: 'dB rise / 20 ms',
    max: 40,
    format: dB,
    marks: [
      { at: 15, label: 'plosive' },
      { at: 20, label: 'click' },
    ],
  },
  {
    key: 'flatness',
    label: 'flatness',
    hint: 'tone … noise',
    format: fixed(3),
    hold: 'min',
    marks: [
      { at: 0.15, label: 'whistle' },
      { at: 0.7, label: 'rasp' },
    ],
  },
  {
    key: 'zcr',
    label: 'zcr',
    hint: 'voiced … fricative',
    format: fixed(3),
    marks: [{ at: 0.6, label: 'sss' }],
  },
  {
    key: 'periodicity',
    label: 'periodicity',
    hint: 'growl … whistle',
    format: fixed(3),
    marks: [
      { at: 0.5, label: 'growl' },
      { at: 0.85, label: 'whistle' },
    ],
  },
  {
    key: 'f0',
    label: 'f0',
    hint: 'pitch',
    max: 3200,
    format: hz,
    marks: [
      { at: 80, label: 'growl' },
      { at: 800, label: 'whistle' },
    ],
  },
  { key: 'centroid', label: 'centroid', hint: 'spectral mean', max: 8000, format: hz },
];

/**
 * Phase 5: the envelope's modulation, one row per thing the detector weighs.
 *
 * `stability` is first because it is the one that decides. The rate is only
 * interesting once the rate has stopped moving.
 */
export const TRILL_METERS = [
  {
    key: 'stability',
    label: 'stability',
    hint: 'the discriminator',
    format: fixed(2),
    marks: [{ at: 0.5, label: 'commit' }],
  },
  {
    key: 'modRate',
    label: 'modRate',
    hint: 'envelope pulse',
    min: 18,
    max: 36,
    format: (v) => `${v.toFixed(1)} Hz`,
  },
  {
    key: 'ratio',
    label: 'peak/median',
    hint: 'stands out of its band',
    max: 5,
    format: fixed(2),
    marks: [{ at: 1.8, label: 'gate' }],
  },
  {
    key: 'depth',
    label: 'depth',
    hint: 'how far it pulses',
    max: 1,
    format: fixed(3),
    marks: [{ at: 0.05, label: 'floor' }],
  },
];

export class Readout {
  /**
   * @param {HTMLElement} root
   * @param {object[]} [meters] one of the exported definitions
   */
  constructor(root, meters = MOUTH_METERS) {
    this.root = root;
    this.meters = meters;
    /** @type {Map<string, object>} */
    this.rows = new Map();

    const frag = document.createDocumentFragment();
    for (const m of meters) {
      const min = m.min ?? 0;
      const max = m.max ?? 1;
      const format = m.format ?? fixed(3);

      const row = document.createElement('div');
      row.className = 'meter';

      const label = document.createElement('span');
      label.className = 'meter-label';
      label.textContent = m.label;

      const track = document.createElement('span');
      track.className = 'meter-track';

      const fill = document.createElement('span');
      fill.className = 'meter-fill';
      track.append(fill);

      for (const mark of m.marks ?? []) {
        const tick = document.createElement('span');
        tick.className = 'meter-mark';
        tick.style.left = `${((mark.at - min) / (max - min)) * 100}%`;
        tick.dataset.label = mark.label;
        track.append(tick);
      }

      // The held extreme, drawn on the same bar so the transient and the live
      // value are read against one scale.
      let peak = null;
      if (m.hold) {
        peak = document.createElement('span');
        peak.className = 'meter-peak';
        peak.hidden = true;
        track.append(peak);
      }

      const value = document.createElement('span');
      value.className = 'meter-value';
      value.textContent = '—';

      const hint = document.createElement('span');
      hint.className = 'meter-hint';
      hint.textContent = m.hint ?? '';

      row.append(label, track, value, hint);
      frag.append(row);
      this.rows.set(m.key, {
        def: m,
        min,
        max,
        format,
        fill,
        value,
        hint,
        peak,
        lo: null,
        hi: null,
      });
    }
    root.replaceChildren(frag);
  }

  /** Extremes seen since the last reset, keyed by meter. */
  extremes(key) {
    const r = this.rows.get(key);
    return r ? { lo: r.lo, hi: r.hi } : { lo: null, hi: null };
  }

  resetPeaks() {
    for (const r of this.rows.values()) {
      r.lo = null;
      r.hi = null;
      if (r.peak) r.peak.hidden = true;
      r.hint.textContent = r.def.hint ?? '';
    }
  }

  /**
   * @param {object|null} sample
   * @param {object} [o]
   * @param {boolean} [o.hold] whether this sample is worth remembering. False
   *   for a frame below the noise gate: its zeroed flatness is the absence of a
   *   measurement, and holding it as a minimum would pass the whistle criterion
   *   on silence.
   * @param {boolean} [o.draw] whether to touch the DOM.
   *
   *   The two are separate because they run at different rates. Accumulating an
   *   extreme is a comparison; painting it is fifty style writes across two
   *   meter racks, and doing that sixty times a second was a real part of why
   *   the render loop dropped to 13 Hz. Extremes are still collected from every
   *   frame — that is the whole point of peak-hold — while the bars repaint at
   *   a rate an eye can read.
   */
  update(sample, { hold = true, draw = true } = {}) {
    for (const r of this.rows.values()) {
      const v = sample?.[r.def.key];
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        if (draw) {
          r.fill.style.width = '0%';
          r.value.textContent = '—';
        }
        continue;
      }

      if (hold) {
        if (r.lo === null || v < r.lo) r.lo = v;
        if (r.hi === null || v > r.hi) r.hi = v;
      }
      if (!draw) continue;

      const norm = Math.max(0, Math.min(1, (v - r.min) / (r.max - r.min)));
      r.fill.style.width = `${norm * 100}%`;
      r.value.textContent = r.format(v);

      if (r.peak && r.lo !== null) {
        const held = r.def.hold === 'min' ? r.lo : r.hi;
        const heldNorm = Math.max(0, Math.min(1, (held - r.min) / (r.max - r.min)));
        r.peak.hidden = false;
        r.peak.style.left = `${heldNorm * 100}%`;
        r.hint.textContent = `${r.def.hint ?? ''} · ${r.def.hold} ${r.format(held)}`;
      }
    }
  }
}

export default Readout;
